import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  useTheme,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ClearIcon from '@mui/icons-material/Clear';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ShareIcon from '@mui/icons-material/Share';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import axios from '../utils/axios';
import ReactECharts from 'echarts-for-react';
import 'echarts-wordcloud';
import html2canvas from 'html2canvas';
import { LongHuBangSEO } from '../components/SEOPages';

const DataTable = () => {
  // 根据当前路径判断显示哪个SEO组件
  const currentPath = window.location.pathname;
  const isLongHuBangPage = currentPath === '/longhubang' || currentPath === '/';

  return (
    <>
      {isLongHuBangPage && <LongHuBangSEO />}
      <DataTableContent />
    </>
  );
};

const DataTableContent = () => {
  const theme = useTheme();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [serverPage, setServerPage] = useState(1);
  const [dateError, setDateError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [searchForm, setSearchForm] = useState({
    dateRange: [dayjs().subtract(7, 'day'), dayjs().add(1, 'day')],
    stockKeyword: '',
    traderKeyword: '',
    within3Days: ''
  });
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  // 资金风向标相关状态
  const [fundFlowData, setFundFlowData] = useState([]);
  const [fundFlowLoading, setFundFlowLoading] = useState(false);
  const [fundFlowError, setFundFlowError] = useState(null);
  const [fundFlowRef, setFundFlowRef] = useState(null);
  
  // 热门股票词云相关状态
  const [hotStocksData, setHotStocksData] = useState([]);
  const [hotStocksLoading, setHotStocksLoading] = useState(false);
  const [hotStocksError, setHotStocksError] = useState(null);
  
  // 股票列表模块的引用
  const stockListRef = useRef(null);
  
  // 处理分享按钮点击事件
  const handleShareClick = async () => {
    if (!fundFlowRef) return;
    
    try {
      // 创建截图
      const canvas = await html2canvas(fundFlowRef, {
        scale: 2, // 提高截图质量
        logging: false,
        useCORS: true,
        allowTaint: true,
        backgroundColor: null, // 确保背景透明度正确处理
        onclone: (documentClone) => {
          // 在克隆的DOM上进行任何必要的修改
          const clonedElement = documentClone.querySelector('#fundFlowElement');
          if (clonedElement) {
            // 可以在这里对克隆的元素进行修改
          }
        }
      });
      
      // 创建一个新的canvas来添加水印
      const watermarkedCanvas = document.createElement('canvas');
      const ctx = watermarkedCanvas.getContext('2d');
      
      // 设置canvas大小与原图相同
      watermarkedCanvas.width = canvas.width;
      watermarkedCanvas.height = canvas.height;
      
      // 绘制原始截图
      ctx.drawImage(canvas, 0, 0);
      
      // 计算水印大小 - 占据图片70%的宽度
      const watermarkWidth = canvas.width * 0.7;
      const fontSize = Math.floor(watermarkWidth / 15); // 根据水印宽度计算合适的字体大小
      
      // 添加水印
      ctx.save(); // 保存当前状态
      ctx.globalAlpha = 0.5; // 提高水印透明度使其更清晰
      ctx.font = `bold ${fontSize}px Arial`;
      ctx.fillStyle = '#555555'; // 使用深灰色，更加清晰
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      // 在中间位置添加水印文字
      ctx.translate(canvas.width / 2, canvas.height / 2);
      ctx.rotate(-Math.PI / 12); // 稍微倾斜水印
      ctx.fillText('stockSignals.cn 助您投资顺利', 0, 0);
      ctx.restore(); // 恢复到保存的状态
      
      // 将canvas转换为图片并下载
      try {
        // 确保canvas内容有效
        if (watermarkedCanvas.width <= 0 || watermarkedCanvas.height <= 0) {
          throw new Error('Canvas尺寸无效');
        }
        
        // 使用try-catch包装toDataURL调用，处理可能的跨域问题
        let dataUrl;
        try {
          dataUrl = watermarkedCanvas.toDataURL('image/png');
          
          // 验证dataUrl是否有效
          if (!dataUrl || !dataUrl.startsWith('data:image/png')) {
            throw new Error('生成的图片数据无效');
          }
        } catch (dataUrlError) {
          console.error('生成图片URL失败:', dataUrlError);
          throw new Error('无法生成图片，可能是由于跨域资源限制');
        }
        
        // 创建下载链接
        const link = document.createElement('a');
        link.download = `资金风向标_${new Date().toLocaleDateString()}.png`;
        link.href = dataUrl;
        link.style.display = 'none'; // 隐藏链接
        
        // 安全地添加到DOM并触发点击
        document.body.appendChild(link);
        setTimeout(() => { // 使用setTimeout确保DOM操作完成
          try {
            link.click();
            setTimeout(() => {
              document.body.removeChild(link); // 延迟移除链接
              setSnackbarMessage('截图已保存');
              setSnackbarOpen(true);
            }, 100);
          } catch (clickError) {
            console.error('触发下载失败:', clickError);
            document.body.removeChild(link);
            throw new Error('下载触发失败');
          }
        }, 0);
      } catch (downloadError) {
        console.error('截图生成或下载失败:', downloadError);
        setSnackbarMessage(`截图处理失败: ${downloadError.message || '未知错误'}`);
        setSnackbarOpen(true);
      }
    } catch (error) {
      console.error('截图生成失败:', error);
      setSnackbarMessage('截图生成失败，请稍后再试');
      setSnackbarOpen(true);
    }
  };
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // 检测是否为移动端
  const isMobile = theme.breakpoints.down('sm');
  
  const columns = [
    { id: 'trade_date', label: '日期', minWidth: isMobile ? 80 : 100 },
    { id: 'stock_code', label: '股票代码', minWidth: isMobile ? 80 : 100 },
    { id: 'stock_name', label: '股票名称', minWidth: isMobile ? 90 : 120 },
    { id: 'trader_name', label: '游资名称', minWidth: isMobile ? 80 : 150 },
    { id: 'net_buy_amount', label: '净买入(万)', minWidth: isMobile ? 90 : 120, align: 'right', format: (value) => Math.round(value).toLocaleString('zh-CN', { maximumFractionDigits: 0 }) },
    { 
      id: 'within_3_days', 
      label: (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          近3日
          <Tooltip title="点击查看说明" arrow>
            <IconButton 
              size="small" 
              onClick={() => setHelpDialogOpen(true)}
              sx={{ 
                p: 0.2, 
                color: theme.palette.primary.main,
                '&:hover': {
                  backgroundColor: theme.palette.primary.main + '20',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <HelpOutlineIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Box>
      ), 
      minWidth: isMobile ? 80 : 120 
    },
    { id: 'concept', label: '概念', minWidth: isMobile ? 100 : 150 },
    { id: 'limit_reason', label: '涨跌停原因', minWidth: isMobile ? 120 : 200 }
  ];

  // 处理股票代码点击跳转
  const handleStockCodeClick = (stockCode) => {
    // 跳转到东方财富股票页面
    const url = `https://quote.eastmoney.com/${stockCode}.html`;
    window.open(url, '_blank');
  };

  // 获取资金风向标数据
  const fetchFundFlowData = async () => {
    setFundFlowLoading(true);
    setFundFlowError(null);
    try {
      // 检查日期范围
      if (!validateDates()) {
        setFundFlowLoading(false);
        return;
      }

      // 安全地格式化日期，确保日期对象有效
      const startDate = searchForm.dateRange[0] && dayjs.isDayjs(searchForm.dateRange[0]) && searchForm.dateRange[0].isValid() 
        ? searchForm.dateRange[0].format('YYYY-MM-DD') 
        : dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      const endDate = searchForm.dateRange[1] && dayjs.isDayjs(searchForm.dateRange[1]) && searchForm.dateRange[1].isValid() 
        ? searchForm.dateRange[1].format('YYYY-MM-DD') 
        : dayjs().format('YYYY-MM-DD');
      const stockKeyword = searchForm.stockKeyword || '';
      const traderKeyword = searchForm.traderKeyword || '';

      const response = await axios.get('/longhubang/fund-flow', {
        params: { 
          start_date: startDate, 
          end_date: endDate, 
          stock_code: stockKeyword, 
          trader_name: traderKeyword,
          limit: 10
        }
      });

      if (response.data && response.data.fund_flow_data) {
        setFundFlowData(response.data.fund_flow_data);
      } else {
        setFundFlowData([]);
      }
    } catch (err) {
      console.error('获取资金风向标数据失败:', err);
      setFundFlowError(err.message || '获取资金风向标数据失败');
      setFundFlowData([]);
    } finally {
      setFundFlowLoading(false);
    }
  };

  // 获取热门股票词云数据
  const fetchHotStocksData = async () => {
    setHotStocksLoading(true);
    setHotStocksError(null);
    try {
      // 检查日期范围
      if (!validateDates()) {
        setHotStocksLoading(false);
        return;
      }

      // 安全地格式化日期，确保日期对象有效
      const startDate = searchForm.dateRange[0] && dayjs.isDayjs(searchForm.dateRange[0]) && searchForm.dateRange[0].isValid() 
        ? searchForm.dateRange[0].format('YYYY-MM-DD') 
        : dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      const endDate = searchForm.dateRange[1] && dayjs.isDayjs(searchForm.dateRange[1]) && searchForm.dateRange[1].isValid() 
        ? searchForm.dateRange[1].format('YYYY-MM-DD') 
        : dayjs().format('YYYY-MM-DD');
      const stockKeyword = searchForm.stockKeyword || '';
      const traderKeyword = searchForm.traderKeyword || '';

      const response = await axios.get('/longhubang/hot-stocks-wordcloud', {
        params: { 
          start_date: startDate, 
          end_date: endDate, 
          stock_code: stockKeyword, 
          trader_name: traderKeyword
        }
      });

      if (response.data && response.data.hot_stocks_data) {
        setHotStocksData(response.data.hot_stocks_data);
      } else {
        setHotStocksData([]);
      }
    } catch (err) {
      console.error('获取热门股票词云数据失败:', err);
      setHotStocksError(err.message || '获取热门股票词云数据失败');
      setHotStocksData([]);
    } finally {
      setHotStocksLoading(false);
    }
  };

  // 验证日期选择的合理性
  const validateDates = () => {
    // 检查日期范围是否存在且有效
    if (!searchForm.dateRange || 
        !searchForm.dateRange[0] || 
        !searchForm.dateRange[1] || 
        !dayjs.isDayjs(searchForm.dateRange[0]) || 
        !dayjs.isDayjs(searchForm.dateRange[1]) || 
        !searchForm.dateRange[0].isValid() || 
        !searchForm.dateRange[1].isValid()) {
      setDateError('请选择有效的日期范围');
      setSnackbarOpen(true);
      return false;
    }

    if (searchForm.dateRange[0].isAfter(searchForm.dateRange[1])) {
      setDateError('开始日期不能晚于结束日期');
      setSnackbarOpen(true);
      return false;
    }

    // 检查日期范围是否过大（例如超过1年）
    const daysDiff = searchForm.dateRange[1].diff(searchForm.dateRange[0], 'day');
    if (daysDiff > 365) {
      setDateError('日期范围不能超过1年');
      setSnackbarOpen(true);
      return false;
    }

    return true;
  };

  // 带股票参数的数据获取函数，用于词云点击搜索
  const fetchDataWithStock = async (pageNum = 1, stockName = '', pageSize = rowsPerPage) => {
    console.log(`fetchDataWithStock 被调用，股票名称: ${stockName}, 请求页码: ${pageNum}, 每页行数: ${pageSize}`);
    setLoading(true);
    setError(null);
    try {
      // 检查日期范围
      if (!validateDates()) {
        setLoading(false);
        return;
      }

      // 安全地格式化日期，确保日期对象有效
      const startDate = searchForm.dateRange[0] && dayjs.isDayjs(searchForm.dateRange[0]) && searchForm.dateRange[0].isValid() 
        ? searchForm.dateRange[0].format('YYYY-MM-DD') 
        : dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      const endDate = searchForm.dateRange[1] && dayjs.isDayjs(searchForm.dateRange[1]) && searchForm.dateRange[1].isValid() 
        ? searchForm.dateRange[1].format('YYYY-MM-DD') 
        : dayjs().format('YYYY-MM-DD');
      const traderKeyword = searchForm.traderKeyword || '';
      const within3Days = searchForm.within3Days || '';

      // 获取表格数据和资金风向标数据，使用传入的股票名称
      const [tableResponse] = await Promise.all([
        axios.get('/longhubang/', {
          params: { 
            start_date: startDate, 
            end_date: endDate, 
            stock_code: stockName, 
            trader_name: traderKeyword,
            within_3_days: within3Days,
            page: pageNum,
            page_size: pageSize
          }
        }),
        fetchFundFlowData()
      ]);

      const response = tableResponse;

      // 处理分页响应
      if (response.data && typeof response.data === 'object') {
        const { data: dataArray, total, page, page_size, total_pages } = response.data;
        
        if (Array.isArray(dataArray)) {
          setData(dataArray);
          setTotalCount(total || 0);
          setServerPage(page || 1);
          setTotalPages(total_pages || 1);
          if (pageNum === 1) {
            setPage(0);
          }
        } else {
          setData([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        setData([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      setError('获取数据失败，请稍后重试');
      setData([]);
      setTotalCount(0);
      setTotalPages(0);
    } finally {
      setLoading(false);
      // 数据加载完成后，滚动到股票列表模块
      setTimeout(() => {
        if (stockListRef.current) {
          stockListRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'start' 
          });
        }
      }, 100);
    }
  };

  const fetchData = async (pageNum = serverPage, pageSize = rowsPerPage, shouldScroll = true) => {
    console.log(`fetchData 被调用，请求页码: ${pageNum}, 每页行数: ${pageSize}, 当前前端页码: ${page}, 当前服务器页码: ${serverPage}`);
    setLoading(true);
    setError(null);
    try {
      // 检查日期范围
      if (!validateDates()) {
        setLoading(false);
        return;
      }

      // 安全地格式化日期，确保日期对象有效
      const startDate = searchForm.dateRange[0] && dayjs.isDayjs(searchForm.dateRange[0]) && searchForm.dateRange[0].isValid() 
        ? searchForm.dateRange[0].format('YYYY-MM-DD') 
        : dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      const endDate = searchForm.dateRange[1] && dayjs.isDayjs(searchForm.dateRange[1]) && searchForm.dateRange[1].isValid() 
        ? searchForm.dateRange[1].format('YYYY-MM-DD') 
        : dayjs().format('YYYY-MM-DD');
      const stockKeyword = searchForm.stockKeyword || '';
      const traderKeyword = searchForm.traderKeyword || '';
      const within3Days = searchForm.within3Days || '';

      // 获取表格数据和资金风向标数据
      const [tableResponse] = await Promise.all([
        axios.get('/longhubang/', {
          params: { 
            start_date: startDate, 
            end_date: endDate, 
            stock_code: stockKeyword, 
            trader_name: traderKeyword,
            within_3_days: within3Days,
            page: pageNum,
            page_size: pageSize
          }
        }),
        fetchFundFlowData()
      ]);

      const response = tableResponse;

      // 处理分页响应
      if (response.data && typeof response.data === 'object') {
        const { data: dataArray, total, page, page_size, total_pages } = response.data;
        
        if (Array.isArray(dataArray)) {
          setData(dataArray);
          setTotalCount(total || 0);
          setServerPage(page || 1);
          setTotalPages(total_pages || 1);
          // 只在初始加载或搜索/重置时设置前端页码
          // 翻页操作中不重置前端页码，保持用户当前位置
          if (pageNum === 1) {
            setPage(0); // 只有在请求第一页时重置前端页码
          }
        } else {
          setData([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        setData([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error('获取数据失败:', err);
      setError(err.message || '获取数据失败，请稍后重试');
    } finally {
      setLoading(false);
      // 只有在shouldScroll为true时才滚动到股票列表模块
      if (shouldScroll) {
        setTimeout(() => {
          if (stockListRef.current) {
            stockListRef.current.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }
        }, 100);
      }
    }
  };

  // 创建图表实例引用
  const fundFlowChartRef = useRef(null);
  const hotStocksChartRef = useRef(null);

  useEffect(() => {
    fetchData(1, rowsPerPage, false); // 初始加载时不滚动
    fetchFundFlowData();
    fetchHotStocksData();

    // 添加窗口大小变化监听，以便在窗口大小变化时重新调整图表大小
    const handleResize = () => {
      if (fundFlowChartRef.current) {
        const echartsInstance = fundFlowChartRef.current.getEchartsInstance();
        echartsInstance.resize();
      }
      if (hotStocksChartRef.current) {
        const echartsInstance = hotStocksChartRef.current.getEchartsInstance();
        echartsInstance.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    
    // 初始加载后延迟调用resize以确保图表正确渲染
    const timer = setTimeout(() => {
      handleResize();
    }, 300);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, []);

  const handleChangePage = (event, newPage) => {
    // 计算要请求的服务器页码
    // 前端页码从0开始，后端页码从1开始
    const requestPage = newPage + 1;
    
    // 无论是向前还是向后翻页，都直接请求对应的页码
    if (requestPage >= 1 && requestPage <= totalPages) {
      console.log(`请求页码: ${requestPage}, 总页数: ${totalPages}`);
      setServerPage(requestPage);
      // 直接设置前端页码，确保UI立即更新
      setPage(newPage);
      fetchData(requestPage); // 翻页时需要滚动
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = +event.target.value;
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    setServerPage(1);
    // 在改变每页行数时，需要重新计算总页数
    console.log(`改变每页行数为: ${newRowsPerPage}, 当前总记录数: ${totalCount}`);
    // 让后端重新计算总页数，直接传递新的每页行数
    // 修改fetchData函数调用，确保使用新的rowsPerPage值
    fetchData(1, newRowsPerPage); // 改变每页行数时需要滚动
  };

  const handleSearch = () => {
    // 搜索操作总是从第一页开始
    setPage(0);
    setServerPage(1);
    // 手动搜索时同时更新词云数据
    fetchHotStocksData();
    fetchData(1, rowsPerPage, true); // 搜索时需要滚动
  };

  const handleReset = () => {
    // 创建重置后的表单值
    const resetForm = {
      dateRange: [dayjs().subtract(7, 'day'), dayjs().add(1, 'day')],
      stockKeyword: '',
      traderKeyword: '',
      within3Days: ''
    };
    
    // 清除所有状态
    setDateError('');
    setSnackbarOpen(false);
    setError(null);
    
    // 直接使用重置后的表单值调用API，绕过验证
    // 安全地格式化日期，确保日期对象有效
    const startDate = resetForm.dateRange[0] && dayjs.isDayjs(resetForm.dateRange[0]) && resetForm.dateRange[0].isValid() 
      ? resetForm.dateRange[0].format('YYYY-MM-DD') 
      : dayjs().subtract(7, 'day').format('YYYY-MM-DD');
    const endDate = resetForm.dateRange[1] && dayjs.isDayjs(resetForm.dateRange[1]) && resetForm.dateRange[1].isValid() 
      ? resetForm.dateRange[1].format('YYYY-MM-DD') 
      : dayjs().format('YYYY-MM-DD');
    
    // 更新状态
    setSearchForm(resetForm);
    setPage(0);
    setServerPage(1);
    
    // 直接发起请求，不通过fetchData函数
    setLoading(true);
    
    // 定义一个函数来获取词云和饼图数据，使用重置后的表单值
    const fetchChartsData = () => {
      // 使用重置后的表单值直接构建请求参数，而不是依赖searchForm状态
      axios.get('/longhubang/hot-stocks-wordcloud', {
        params: { 
          start_date: startDate, 
          end_date: endDate, 
          stock_code: '', 
          trader_name: ''
        }
      })
      .then(response => {
        if (response.data && response.data.hot_stocks_data) {
          setHotStocksData(response.data.hot_stocks_data);
        } else {
          setHotStocksData([]);
        }
      })
      .catch(err => {
        console.error('重置后获取热门股票词云数据失败:', err);
        setHotStocksError(err.message || '获取热门股票词云数据失败');
        setHotStocksData([]);
      });
      
      axios.get('/longhubang/fund-flow', {
        params: { 
          start_date: startDate, 
          end_date: endDate, 
          stock_code: '', 
          trader_name: '',
          limit: 10
        }
      })
      .then(response => {
        if (response.data && response.data.fund_flow_data) {
          setFundFlowData(response.data.fund_flow_data);
        } else {
          setFundFlowData([]);
        }
      })
      .catch(err => {
        console.error('重置后获取资金风向标数据失败:', err);
        setFundFlowError(err.message || '获取资金风向标数据失败');
        setFundFlowData([]);
      });
    };
    
    axios.get('/longhubang/', {
      params: { 
        start_date: startDate, 
        end_date: endDate, 
        stock_code: '', 
        trader_name: '',
        within_3_days: '',
        page: 1,
        page_size: rowsPerPage
      }
    })
    .then(response => {
      if (response.data && typeof response.data === 'object') {
        const { data: dataArray, total, page, total_pages } = response.data;
        
        if (Array.isArray(dataArray)) {
          setData(dataArray);
          setTotalCount(total || 0);
          setServerPage(page || 1);
          setTotalPages(total_pages || 1);
          setPage(0); // 重置为第一页
        } else {
          setData([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      }
      
      // 在表格数据加载完成后获取词云和饼图数据
      fetchChartsData();
    })
    .catch(err => {
      console.error('重置后获取数据失败:', err);
      setError(err.message || '获取数据失败，请稍后重试');
      
      // 即使表格数据加载失败，也尝试获取词云和饼图数据
      fetchChartsData();
    })
    .finally(() => {
      setLoading(false);
    });
  };

  // 处理词云点击事件
  const handleWordCloudClick = (params) => {
    if (params.data && params.data.name) {
      const stockName = params.data.name;
      
      // 将股票名称填入搜索框
      setSearchForm(prev => ({
        ...prev,
        stockKeyword: stockName
      }));
      
      // 执行搜索但不更新词云，使用最新的股票名称
      setTimeout(() => {
        setPage(0);
        setServerPage(1);
        fetchDataWithStock(1, stockName);
      }, 100);
    }
  };

  // 处理饼图点击事件
  const handlePieChartClick = (params) => {
    if (params.data && params.data.name) {
      const traderName = params.data.name;
      
      // 将游资名称填入搜索框
      setSearchForm(prev => ({
        ...prev,
        traderKeyword: traderName
      }));
      
      // 执行搜索但不更新饼图，使用最新的游资名称
      setTimeout(() => {
        setPage(0);
        setServerPage(1);
        fetchDataWithTrader(1, traderName);
      }, 100);
    }
  };
  
  // 带游资参数的数据获取函数，用于饼图点击搜索
  const fetchDataWithTrader = async (pageNum = 1, traderName = '', pageSize = rowsPerPage) => {
    console.log(`fetchDataWithTrader 被调用，游资名称: ${traderName}, 请求页码: ${pageNum}, 每页行数: ${pageSize}`);
    setLoading(true);
    setError(null);
    try {
      // 检查日期范围
      if (!validateDates()) {
        setLoading(false);
        return;
      }

      // 安全地格式化日期，确保日期对象有效
      const startDate = searchForm.dateRange[0] && dayjs.isDayjs(searchForm.dateRange[0]) && searchForm.dateRange[0].isValid() 
        ? searchForm.dateRange[0].format('YYYY-MM-DD') 
        : dayjs().subtract(7, 'day').format('YYYY-MM-DD');
      const endDate = searchForm.dateRange[1] && dayjs.isDayjs(searchForm.dateRange[1]) && searchForm.dateRange[1].isValid() 
        ? searchForm.dateRange[1].format('YYYY-MM-DD') 
        : dayjs().format('YYYY-MM-DD');
      const stockKeyword = searchForm.stockKeyword || '';
      const within3Days = searchForm.within3Days || '';

      // 获取表格数据和资金风向标数据，使用传入的游资名称
      const [tableResponse] = await Promise.all([
        axios.get('/longhubang/', {
          params: { 
            start_date: startDate, 
            end_date: endDate, 
            stock_code: stockKeyword, 
            trader_name: traderName,
            within_3_days: within3Days,
            page: pageNum,
            page_size: pageSize
          }
        }),
        fetchFundFlowData()
      ]);

      const response = tableResponse;

      // 处理分页响应
      if (response.data && typeof response.data === 'object') {
        const { data: dataArray, total, page, page_size, total_pages } = response.data;
        
        if (Array.isArray(dataArray)) {
          setData(dataArray);
          setTotalCount(total || 0);
          setServerPage(page || 1);
          setTotalPages(total_pages || 1);
          // 只在初始加载或搜索/重置时设置前端页码
          if (pageNum === 1) {
            setPage(0); // 只有在请求第一页时重置前端页码
          }
        } else {
          setData([]);
          setTotalCount(0);
          setTotalPages(0);
        }
      } else {
        setData([]);
        setTotalCount(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error('获取数据失败:', err);
      setError(err.message || '获取数据失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  // 生成热门股票词云配置
  // 使用useCallback缓存词云图配置，避免不必要的重新计算和渲染
  const getHotStocksWordCloudOption = useCallback(() => {
    if (!hotStocksData || hotStocksData.length === 0) {
      return {
        title: {
          text: '暂无数据',
          left: 'center',
          top: 'center',
          textStyle: {
            color: theme.palette.text.secondary,
            fontSize: 14
          }
        }
      };
    }

    // 处理词云数据
    const wordCloudData = hotStocksData.map((item) => {
      const isPositive = item.total_net_buy >= 0;
      return {
        name: item.stock_name,
        value: item.weight,
        stock_code: item.stock_code,
        total_net_buy: item.total_net_buy,
        textStyle: {
          color: isPositive ? '#ff1744' : '#00c853',
          fontWeight: 'bold'
        }
      };
    });

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(50, 50, 50, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        textStyle: {
          color: '#fff',
          fontSize: 12,
          fontWeight: 'bold'
        },
        formatter: function(params) {
          const data = params.data;
          const sign = data.total_net_buy >= 0 ? '+' : '-';
          const formattedValue = Math.abs(data.total_net_buy).toLocaleString('zh-CN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
          return `${data.name} (${data.stock_code})<br/>净买入: ${sign}${formattedValue}万元<br/>点击可搜索该股票`;
        }
      },
      series: [{
        type: 'wordCloud',
        gridSize: 8,
        sizeRange: [12, 60],
        rotationRange: [-45, 45],
        rotationStep: 15,
        shape: 'pentagon',
        width: '100%',
        height: '100%',
        drawOutOfBound: false,
        // 禁用布局动画，减少闪烁
        layoutAnimation: false,
        textStyle: {
          fontFamily: 'sans-serif',
          fontWeight: 'bold',
          color: function () {
            return 'rgb(' + [
              Math.round(Math.random() * 160),
              Math.round(Math.random() * 160),
              Math.round(Math.random() * 160)
            ].join(',') + ')';
          }
        },
        emphasis: {
          focus: 'self',
          textStyle: {
            shadowBlur: 10,
            shadowColor: '#333'
          }
        },
        data: wordCloudData
      }]
    };
  }, [hotStocksData, theme.palette.text.secondary]);

  // 生成饼图配置
  const getFundFlowChartOption = useCallback(() => {
    if (!fundFlowData || fundFlowData.length === 0) {
      return {
        title: {
          text: '暂无数据',
          left: 'center',
          top: 'center',
          textStyle: {
            color: theme.palette.text.secondary,
            fontSize: 14
          }
        }
      };
    }

    // 处理数据，使用绝对值计算饼图，但保留原始值用于显示
    const chartData = fundFlowData.map((item, index) => {
      const absValue = Math.abs(item.total_net_buy);
      const isNegative = item.total_net_buy < 0;
      
      // 高对比度配色方案 - 红涨绿跌
      const positiveColors = [
        '#ff1744', '#d50000', '#ff5722', '#e91e63', '#f44336',
        '#ff6b35', '#ff3d00', '#dd2c00', '#bf360c', '#b71c1c'
      ];
      const negativeColors = [
        '#00c853', '#00e676', '#4caf50', '#2e7d32', '#1b5e20',
        '#00695c', '#004d40', '#00251a', '#388e3c', '#2e7d32'
      ];
      
      return {
        name: item.trader_name,
        value: absValue,
        originalValue: item.total_net_buy,
        isNegative: isNegative,
        itemStyle: {
          color: isNegative ? negativeColors[index % negativeColors.length] : positiveColors[index % positiveColors.length],
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.2)'
        }
      };
    });

    // 计算总金额用于百分比
    const totalAmount = chartData.reduce((sum, item) => sum + item.value, 0);

    return {
      backgroundColor: 'transparent',
      tooltip: {
        trigger: 'item',
        backgroundColor: 'rgba(50, 50, 50, 0.95)',
        borderColor: 'rgba(255, 255, 255, 0.2)',
        borderWidth: 1,
        textStyle: {
          color: '#fff',
          fontSize: 12,
          fontWeight: 'bold'
        },
        formatter: function(params) {
          const data = params.data;
          const sign = data.isNegative ? '-' : '+';
          const formattedValue = Math.abs(data.originalValue).toLocaleString('zh-CN', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
          });
          const colorDot = `<span style="display:inline-block;margin-right:5px;border-radius:50%;width:10px;height:10px;background-color:${data.itemStyle.color};"></span>`;
          return `${colorDot}${data.name}<br/>净买入: ${sign}${formattedValue}万元<br/>占比: ${params.percent}%<br/>点击可搜索该游资`;
        }
      },
      // 禁用动画，减少闪烁
      animation: false,
      series: [
        {
          name: '净买入金额',
          type: 'pie',
          radius: ['35%', '75%'],
          center: ['50%', '50%'],
          avoidLabelOverlap: true,
          // 启用选中效果
          selectedMode: 'single',
          selectedOffset: 10,
          itemStyle: {
            borderRadius: 10,
            borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.8)',
            borderWidth: 2,
            shadowBlur: 15,
            shadowColor: 'rgba(0, 0, 0, 0.3)'
          },
          label: {
            show: true,
            position: 'outside',
            formatter: function(params) {
              const data = params.data;
              const sign = data.isNegative ? '-' : '+';
              const formattedValue = Math.abs(data.originalValue).toLocaleString('zh-CN', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              });
              return `{name|${data.name}}\n{value|${sign}${formattedValue}万}\n{percent|${params.percent}%}`;
            },
            rich: {
              name: {
                fontSize: 12,
                fontWeight: 'bold',
                color: theme.palette.text.primary,
                lineHeight: 20
              },
              value: {
                fontSize: 11,
                fontWeight: 'normal',
                color: theme.palette.text.secondary,
                lineHeight: 18
              },
              percent: {
                fontSize: 10,
                fontWeight: 'bold',
                color: theme.palette.primary.main,
                lineHeight: 16
              }
            }
          },
          labelLine: {
            show: true,
            length: 20,
            length2: 15,
            smooth: true,
            lineStyle: {
              color: theme.palette.text.secondary,
              width: 2,
              type: 'solid'
            }
          },
          emphasis: {
            scale: true,
            scaleSize: 10,
            itemStyle: {
              shadowBlur: 20,
              shadowOffsetX: 0,
              shadowOffsetY: 5,
              shadowColor: 'rgba(0, 0, 0, 0.4)'
            },
            label: {
              fontSize: 14,
              fontWeight: 'bold'
            }
          },
          data: chartData
        }
      ]
    };
  }, [fundFlowData, theme.palette.mode, theme.palette.text.primary, theme.palette.text.secondary, theme.palette.primary.main]);

  return (
    <>
    {/* 资金风向标模块 - 页面上方 */}
    <Box sx={{ mb: 3 }} ref={setFundFlowRef} id="fundFlowElement">
      <Box sx={{ width: '100%' }}>
        <Card sx={{ 
          borderRadius: '16px', 
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
          background: `linear-gradient(135deg, ${theme.palette.primary.main}10, ${theme.palette.primary.main}05)`
        }}>
          <CardHeader
            title={
              <Box sx={{ 
                display: 'flex', 
                flexDirection: { xs: 'column', sm: 'row' }, 
                alignItems: { xs: 'flex-start', sm: 'center' }, 
                justifyContent: 'space-between', 
                width: '100%',
                gap: { xs: 2, sm: 0 }
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUpIcon color="primary" />
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    资金风向标
                  </Typography>
                  {/* 添加统计时间显示 */}
                  <Typography variant="caption" color="text.secondary" sx={{ ml: 1, fontSize: '0.75rem' }}>
                    ({searchForm.dateRange[0] && dayjs.isDayjs(searchForm.dateRange[0]) && searchForm.dateRange[0].isValid() 
                      ? searchForm.dateRange[0].format('YYYY-MM-DD') 
                      : dayjs().subtract(7, 'day').format('YYYY-MM-DD')} 至 
                     {searchForm.dateRange[1] && dayjs.isDayjs(searchForm.dateRange[1]) && searchForm.dateRange[1].isValid() 
                      ? searchForm.dateRange[1].format('YYYY-MM-DD') 
                      : dayjs().format('YYYY-MM-DD')})
                  </Typography>
                </Box>
                
                {/* 游资净买入金额分布、总净流入、净流入家数、净流出家数 - 响应式布局 */}
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'row',
                  flexWrap: { xs: 'wrap', md: 'nowrap' },
                  gap: { xs: 1, sm: 2 },
                  alignItems: 'center',
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'space-between', sm: 'flex-end' }
                }}>
                  <Box sx={{ textAlign: 'center', display: { xs: 'none', sm: 'block' } }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      游资净买入金额分布
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    px: { xs: 1, sm: 1.5 }, 
                    py: 0.5,
                    borderRadius: 1, 
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}`,
                    textAlign: 'center',
                    flex: { xs: '1 0 30%', sm: 'none' }
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                      总净流入
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        fontWeight: 'bold',
                        color: fundFlowData && fundFlowData.reduce((sum, item) => sum + item.total_net_buy, 0) >= 0 ? '#4caf50' : '#f44336',
                        fontSize: { xs: '0.75rem', sm: '0.8rem' }
                      }}
                    >
                      {fundFlowData && (() => {
                        const totalValue = fundFlowData.reduce((sum, item) => sum + item.total_net_buy, 0);
                        const sign = totalValue >= 0 ? '+' : '-';
                        const formattedValue = Math.abs(totalValue).toLocaleString('zh-CN', {
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        });
                        return `${sign}${formattedValue}万`;
                      })()}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    px: { xs: 1, sm: 1.5 }, 
                    py: 0.5,
                    borderRadius: 1, 
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(76, 175, 80, 0.05)',
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.3)' : 'rgba(76, 175, 80, 0.2)'}`,
                    textAlign: 'center',
                    flex: { xs: '1 0 30%', sm: 'none' }
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                      净流入家数
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#4caf50', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                      {fundFlowData && fundFlowData.filter(item => item.total_net_buy > 0).length}家
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    px: { xs: 1, sm: 1.5 }, 
                    py: 0.5,
                    borderRadius: 1, 
                    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.05)',
                    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.3)' : 'rgba(244, 67, 54, 0.2)'}`,
                    textAlign: 'center',
                    flex: { xs: '1 0 30%', sm: 'none' }
                  }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.7rem' }}>
                      净流出家数
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#f44336', fontSize: { xs: '0.75rem', sm: '0.8rem' } }}>
                      {fundFlowData && fundFlowData.filter(item => item.total_net_buy < 0).length}家
                    </Typography>
                  </Box>
                  {/* 添加分享按钮 - 放在净流出家数右侧，在移动端不显示 */}
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Tooltip title="分享截图">
                      <IconButton 
                        onClick={handleShareClick}
                        size="small"
                        sx={{ 
                          color: theme.palette.primary.main,
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(25, 118, 210, 0.1)',
                          '&:hover': {
                            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.2)' : 'rgba(25, 118, 210, 0.2)',
                          },
                          ml: 1
                        }}
                      >
                        <ShareIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </Box>
            }
            sx={{
              pb: 1,
              '& .MuiCardHeader-title': {
                fontSize: '1.1rem',
                width: '100%'
              },
              '& .MuiCardHeader-content': {
                width: '100%'
              }
            }}
          />
          <CardContent sx={{ pt: 0 }}>
             {fundFlowLoading ? (
               <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                 <CircularProgress size={40} />
               </Box>
             ) : fundFlowError ? (
               <Alert severity="error" sx={{ mb: 2 }}>
                 {fundFlowError}
               </Alert>
             ) : fundFlowData && fundFlowData.length > 0 ? (
                <>
                    {/* 统计信息已移至标题右侧 */}
                    
                    {/* 饼图和词云左右排列展示 - Dashboard布局 */}
                    <Box sx={{ mt: 2, mb: 2, width: '100%' }}>
                      <Grid container spacing={3} sx={{ 
                        display: 'grid', 
                        gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' }, 
                        gap: '24px', 
                        width: '100%', 
                        margin: '0 auto', 
                        px: { xs: 1, sm: 2 } 
                      }}>
                        {/* 游资风向标区域 */}
                        <Grid item>
                          <Card sx={{ 
                            height: '100%',
                            borderRadius: '12px', 
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden'
                          }}>
                            <Box sx={{ 
                              p: 3,
                              display: 'flex',
                              flexDirection: 'column',
                              height: '100%'
                            }}>
                              <Box sx={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 2,
                                width: '100%'
                              }}>
                                <Typography variant="h6" sx={{ 
                                  fontWeight: 'bold',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1
                                }}>
                                  <TrendingUpIcon color="primary" />
                                  游资风向标
                                </Typography>
                              </Box>
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                alignItems: 'center',
                                flexGrow: 1
                              }}>
                                <Box sx={{ 
                                  height: '100%',
                                  width: '100%',
                                  maxWidth: '600px'
                                }}>
                                  <ReactECharts
                                    ref={fundFlowChartRef}
                                    option={getFundFlowChartOption()}
                                    style={{ height: '100%', width: '100%' }}
                                    theme={theme.palette.mode}
                                    opts={{ renderer: 'canvas' }}
                                    onEvents={{
                                      'click': handlePieChartClick
                                    }}
                                  />
                                </Box>
                              </Box>
                            </Box>
                          </Card>
                        </Grid>
                        
                        {/* 热门股票词云区域 */}
                        <Grid item>
                          <Card sx={{ 
                            height: '100%',
                            borderRadius: '12px', 
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                            overflow: 'hidden'
                          }}>
                            <Box sx={{ 
                              p: 3,
                              display: 'flex',
                              flexDirection: 'column',
                              height: '100%'
                            }}>
                          <Typography variant="h6" sx={{ 
                            mb: 2, 
                            fontWeight: 'bold',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                          }}>
                            <TrendingUpIcon color="primary" />
                            热门股票词云
                          </Typography>
                          
                          {/* 净流入正数前五和负数前五股票展示 */}
                          {hotStocksData && hotStocksData.length > 0 && (
                            <Box sx={{ mb: 2, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: { xs: 1, sm: 2 } }}>
                              {/* 净流入正数前五 */}
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ 
                                  fontWeight: 'bold', 
                                  color: '#ff1744',
                                  mb: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}>
                                  <ArrowUpwardIcon fontSize="small" />
                                  净流入前五
                                </Typography>
                                <Box sx={{ 
                                  border: '1px solid rgba(0, 0, 0, 0.12)', 
                                  borderRadius: 1, 
                                  p: { xs: 0.5, sm: 1 },
                                  bgcolor: 'rgba(255, 23, 68, 0.05)'
                                }}>
                                  {hotStocksData
                                    .filter(item => item.total_net_buy > 0)
                                    .sort((a, b) => b.total_net_buy - a.total_net_buy)
                                    .slice(0, 5)
                                    .map((item, index) => (
                                      <Box key={index} sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: index < 4 ? 0.5 : 0,
                                        fontSize: { xs: '0.75rem', sm: '0.85rem' }
                                      }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                          {item.stock_name} ({item.stock_code})
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#ff1744', fontWeight: 'bold' }}>
                                          +{Math.abs(item.total_net_buy).toLocaleString('zh-CN', {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0
                                          })}万
                                        </Typography>
                                      </Box>
                                    ))
                                  }
                                  {hotStocksData.filter(item => item.total_net_buy > 0).length === 0 && (
                                    <Typography variant="body2" sx={{ textAlign: 'center', py: 1, color: 'text.secondary' }}>
                                      暂无数据
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              
                              {/* 净流入负数前五 */}
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="subtitle2" sx={{ 
                                  fontWeight: 'bold', 
                                  color: '#00c853',
                                  mb: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5
                                }}>
                                  <ArrowDownwardIcon fontSize="small" />
                                  净流出前五
                                </Typography>
                                <Box sx={{ 
                                  border: '1px solid rgba(0, 0, 0, 0.12)', 
                                  borderRadius: 1, 
                                  p: { xs: 0.5, sm: 1 },
                                  bgcolor: 'rgba(0, 200, 83, 0.05)'
                                }}>
                                  {hotStocksData
                                    .filter(item => item.total_net_buy < 0)
                                    .sort((a, b) => a.total_net_buy - b.total_net_buy)
                                    .slice(0, 5)
                                    .map((item, index) => (
                                      <Box key={index} sx={{ 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: index < 4 ? 0.5 : 0,
                                        fontSize: { xs: '0.75rem', sm: '0.85rem' }
                                      }}>
                                        <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                          {item.stock_name} ({item.stock_code})
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: '#00c853', fontWeight: 'bold' }}>
                                          -{Math.abs(item.total_net_buy).toLocaleString('zh-CN', {
                                            minimumFractionDigits: 0,
                                            maximumFractionDigits: 0
                                          })}万
                                        </Typography>
                                      </Box>
                                    ))
                                  }
                                  {hotStocksData.filter(item => item.total_net_buy < 0).length === 0 && (
                                    <Typography variant="body2" sx={{ textAlign: 'center', py: 1, color: 'text.secondary' }}>
                                      暂无数据
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            点击股票名称可快速搜索该股票数据
                          </Typography>
                          
                          <Box sx={{ 
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                            {hotStocksLoading ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress size={40} />
                              </Box>
                            ) : hotStocksError ? (
                              <Alert severity="error" sx={{ mb: 2 }}>
                                {hotStocksError}
                              </Alert>
                            ) : hotStocksData && hotStocksData.length > 0 ? (
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center',
                                alignItems: 'center',
                                height: { xs: 250, sm: 300, md: 350 },
                                mt: { xs: 1, sm: 2 }
                              }}>
                                <Box sx={{ 
                                  height: '100%',
                                  width: '100%',
                                  maxWidth: '500px'
                                }}>
                                  <ReactECharts
                                    ref={hotStocksChartRef}
                                    option={getHotStocksWordCloudOption()}
                                    style={{ height: '100%', width: '100%' }}
                                    theme={theme.palette.mode}
                                    opts={{ renderer: 'canvas' }}
                                    onEvents={{
                                      'click': handleWordCloudClick
                                    }}
                                  />
                                </Box>
                              </Box>
                            ) : (
                              <Box sx={{ textAlign: 'center', py: 4 }}>
                                <Typography variant="body1" color="text.secondary">
                                  暂无热门股票数据
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Card>
                        </Grid>
                      </Grid>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      暂无资金流向数据
                    </Typography>
                  </Box>
                )
              }
           </CardContent>
        </Card>
      </Box>
    </Box>
    
    {/* 数据表格区域 */}
     <Box ref={stockListRef}>
          <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: '16px', boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)' }}>
      <Box sx={{ p: 2, backgroundColor: theme.palette.mode === 'dark' ? 'rgba(25, 118, 210, 0.05)' : '#f5f7fa', borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#e3f2fd'}`, borderTopLeftRadius: '16px', borderTopRightRadius: '16px' }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="zh-cn">
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={3} md={2}>
              <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: theme.palette.mode === 'dark' ? theme.palette.primary.light : '#1565c0', display: 'flex', alignItems: 'center', fontSize: '1rem' }}>
                <Box component="span" sx={{ mr: 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 24, height: 24, borderRadius: '50%', backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText, fontSize: '0.8rem', fontWeight: 'bold' }}>龙</Box>
                龙虎榜数据
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <DatePicker
                  label="开始日期"
                  value={searchForm.dateRange[0]}
                  onChange={(newValue) => {
                    // 确保newValue是有效的dayjs对象
                    if (newValue && dayjs.isDayjs(newValue) && newValue.isValid()) {
                      setSearchForm({ ...searchForm, dateRange: [newValue, searchForm.dateRange[1]] });
                    } else {
                      // 如果输入无效，保持原值不变
                      console.warn('无效的开始日期输入');
                    }
                    setDateError('');
                    // 不要在这里调用fetchHotStocksData或fetchFundFlowData
                  }}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: false,
                      sx: {
                        '& .MuiInputBase-root': {
                          height: '32px',
                          fontSize: '0.8rem',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.8rem',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '7px 14px',
                          fontSize: '0.8rem',
                        },
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }
                    }
                  }}
                  disableFuture
                  maxDate={searchForm.dateRange[1] || dayjs()}
                />
                <Typography variant="body2" sx={{ color: 'text.secondary', mx: 1 }}>至</Typography>
                <DatePicker
                  label="结束日期"
                  value={searchForm.dateRange[1]}
                  onChange={(newValue) => {
                    // 确保newValue是有效的dayjs对象
                    if (newValue && dayjs.isDayjs(newValue) && newValue.isValid()) {
                      setSearchForm({ ...searchForm, dateRange: [searchForm.dateRange[0], newValue] });
                    } else {
                      // 如果输入无效，保持原值不变
                      console.warn('无效的结束日期输入');
                    }
                    setDateError('');
                    // 不要在这里调用fetchHotStocksData或fetchFundFlowData
                  }}
                  format="YYYY-MM-DD"
                  slotProps={{
                    textField: {
                      size: 'small',
                      error: false,
                      sx: {
                        '& .MuiInputBase-root': {
                          height: '32px',
                          fontSize: '0.8rem',
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: '0.8rem',
                        },
                        '& .MuiOutlinedInput-input': {
                          padding: '7px 14px',
                          fontSize: '0.8rem',
                        },
                        '& .MuiOutlinedInput-root': {
                          '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                            borderColor: 'transparent',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme.palette.primary.main,
                          },
                        },
                      }
                    }
                  }}
                  disableFuture
                  maxDate={dayjs()}
                  minDate={searchForm.dateRange[0]}
                />
              </Box>
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="股票代码/名称"
                variant="outlined"
                size="small"
                value={searchForm.stockKeyword}
                onChange={(e) => {
                  // 只更新状态，不触发重新渲染
                  setSearchForm({ ...searchForm, stockKeyword: e.target.value });
                  // 不要在这里调用fetchHotStocksData或fetchFundFlowData
                }}
                InputProps={{
                  startAdornment: <SearchIcon color="action" sx={{ mr: 0.5, fontSize: '0.9rem' }} />,
                  endAdornment: searchForm.stockKeyword ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="清除股票搜索"
                        onClick={() => {
                          // 只更新状态，不触发重新渲染
                          setSearchForm({ ...searchForm, stockKeyword: '' });
                          // 不要在这里调用fetchHotStocksData或fetchFundFlowData
                        }}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: { height: '32px' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                    fontSize: '0.8rem',
                    transform: 'translate(14px, 8px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '7px 14px',
                    fontSize: '0.8rem',
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} sm={3} md={2}>
              <TextField
                fullWidth
                label="游资搜索"
                variant="outlined"
                size="small"
                value={searchForm.traderKeyword}
                onChange={(e) => {
                  // 只更新状态，不触发重新渲染
                  setSearchForm({ ...searchForm, traderKeyword: e.target.value });
                  // 不要在这里调用fetchHotStocksData或fetchFundFlowData
                }}
                InputProps={{
                  endAdornment: searchForm.traderKeyword ? (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="清除游资搜索"
                        onClick={() => {
                          // 只更新状态，不触发重新渲染
                          setSearchForm({ ...searchForm, traderKeyword: '' });
                          // 不要在这里调用fetchHotStocksData或fetchFundFlowData
                        }}
                        edge="end"
                        size="small"
                      >
                        <ClearIcon sx={{ fontSize: '0.9rem' }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                  sx: { height: '32px' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.palette.primary.main,
                    },
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                    fontSize: '0.8rem',
                    transform: 'translate(14px, 8px) scale(1)',
                    '&.MuiInputLabel-shrink': {
                      transform: 'translate(14px, -6px) scale(0.75)',
                    },
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '7px 14px',
                    fontSize: '0.8rem',
                  },
                }}
              />
            </Grid>
            <Grid item xs={4} sm={2} md={1}>
              <TextField
                fullWidth
                select
                label="近3日"
                variant="outlined"
                size="small"
                value={searchForm.within3Days}
                onChange={(e) => {
                  // 只更新状态，不触发重新渲染
                  setSearchForm({ ...searchForm, within3Days: e.target.value });
                  // 不要在这里调用fetchHotStocksData或fetchFundFlowData
                }}
                SelectProps={{
                  native: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  sx: { height: '32px' },
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '4px',
                  },
                  '& .MuiInputLabel-root': {
                    color: theme.palette.text.secondary,
                    fontSize: '0.8rem',
                  },
                  '& .MuiOutlinedInput-input': {
                    padding: '7px 14px',
                    fontSize: '0.8rem',
                  },
                }}
              >
                <option value="">全部</option>
                <option value="是">是</option>
                <option value="否">否</option>
              </TextField>
            </Grid>
            <Grid item xs={8} sm={4} md={3}>
              <Box sx={{ display: 'flex', gap: 1, height: '100%', alignItems: 'center' }}>
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={handleSearch}
                  startIcon={<SearchIcon sx={{ fontSize: '0.9rem' }} />}
                  sx={{ 
                    borderRadius: '4px', 
                    boxShadow: '0 2px 5px rgba(25, 118, 210, 0.2)',
                    padding: '4px 12px',
                    height: '32px',
                    fontSize: '0.8rem',
                    fontWeight: 'medium',
                    backgroundColor: '#1976d2',
                    '&:hover': {
                      backgroundColor: '#1565c0',
                    }
                  }}
                >
                  搜索
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleReset}
                  startIcon={<RefreshIcon sx={{ fontSize: '0.9rem' }} />}
                  sx={{ 
                    borderRadius: '4px',
                    padding: '4px 12px',
                    height: '32px',
                    fontSize: '0.8rem',
                    fontWeight: 'medium',
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    '&:hover': {
                      borderColor: '#1565c0',
                      backgroundColor: 'rgba(25, 118, 210, 0.04)',
                    }
                  }}
                >
                  重置
                </Button>
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>
      </Box>

      {error && (
        <Box sx={{ p: 2 }}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={dateError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer sx={{ 
            maxHeight: 'calc(100vh - 300px)', 
            '& .MuiTable-root': { 
              borderCollapse: 'separate', 
              borderSpacing: '0 4px',
              minWidth: { xs: '800px', sm: 'auto' } // 移动端设置最小宽度确保水平滚动
            },
            overflowX: 'auto' // 确保水平滚动
          }}>
            <Table stickyHeader aria-label="龙虎榜数据表">
              <TableHead>
                <TableRow sx={{ height: '28px', backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : 'rgba(0, 0, 0, 0.02)', borderBottom: `1px solid ${theme.palette.divider}` }}>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                      sx={{ 
                        fontWeight: 'medium', 
                        fontSize: { xs: '0.7rem', sm: '0.8rem' }, // 移动端使用更小字体
                        color: theme.palette.text.primary,
                        backgroundColor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fafafa',
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        padding: { xs: '4px 6px', sm: '6px 8px' }, // 移动端减小内边距
                        height: '28px'
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((row, index) => {
                  return (
                    <TableRow 
                      hover 
                      role="checkbox" 
                      tabIndex={-1} 
                      key={`${row.trade_date}-${row.stock_code}-${row.trader_name}-${index}`}
                      sx={{ 
                        height: '32px',
                        '&:hover': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)',
                        },
                        '&:nth-of-type(even)': {
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : 'rgba(0, 0, 0, 0.02)',
                        }
                      }}
                    >
                      {columns.map((column) => {
                        const value = row[column.id];
                        let displayValue = value;
                        
                        if (column.id === 'stock_code') {
                          displayValue = (
                            <Chip 
                              label={value}
                              size="small"
                              onClick={() => handleStockCodeClick(value)}
                              sx={{ 
                                fontSize: { xs: '0.6rem', sm: '0.7rem' }, // 移动端更小字体
                                height: { xs: '20px', sm: '24px' }, // 移动端更小高度
                                backgroundColor: '#f5f5f5',
                                color: '#666',
                                cursor: 'pointer',
                                '&:hover': {
                                  backgroundColor: '#1976d2',
                                  color: '#fff'
                                },
                                '& .MuiChip-label': {
                                  padding: { xs: '0 6px', sm: '0 8px' }, // 移动端减小内边距
                                  fontWeight: 'medium'
                                }
                              }}
                            />
                          );
                        } else if (column.id === 'trader_name') {
                          // 处理游资名称显示，将Asking显示为匿名
                          const traderDisplayName = value === 'Asking' ? '匿名' : value;
                          // 在移动端截断过长的游资名称
                          const maxLength = window.innerWidth <= 600 ? 8 : 20;
                          const truncatedName = traderDisplayName.length > maxLength 
                            ? traderDisplayName.substring(0, maxLength) + '...' 
                            : traderDisplayName;
                          displayValue = (
                            <Tooltip title={traderDisplayName} arrow>
                              <span>{truncatedName}</span>
                            </Tooltip>
                          );
                        } else if (column.id === 'within_3_days') {
                          displayValue = (
                            <Chip 
                              label={value} 
                              size="small" 
                              color={value === '是' ? 'success' : 'default'}
                              sx={{ 
                                fontSize: { xs: '0.6rem', sm: '0.7rem' }, // 移动端更小字体
                                height: { xs: '18px', sm: '20px' }, // 移动端更小高度
                                '& .MuiChip-label': {
                                  padding: { xs: '0 4px', sm: '0 6px' } // 移动端减小内边距
                                }
                              }}
                            />
                          );
                        } else if (column.format && typeof value === 'number') {
                          displayValue = column.format(value);
                        }
                        
                        return (
                          <TableCell 
                            key={column.id} 
                            align={column.align}
                            sx={{ 
                              fontSize: { xs: '0.65rem', sm: '0.75rem' }, // 移动端使用更小字体
                              padding: { xs: '3px 6px', sm: '4px 8px' }, // 移动端减小内边距
                              height: '32px',
                              borderBottom: `1px solid ${theme.palette.divider}`,
                              color: column.id === 'net_buy_amount' && value > 0 ? '#d32f2f' : 
                                     column.id === 'net_buy_amount' && value < 0 ? '#2e7d32' : 
                                     theme.palette.text.primary,
                              wordBreak: 'break-all', // 允许长文本换行
                              lineHeight: 1.2 // 调整行高
                            }}
                          >
                            {displayValue}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="每页行数:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} 共 ${count !== -1 ? count : `超过 ${to}`} 条`}
            sx={{
              borderTop: `1px solid ${theme.palette.divider}`,
              '& .MuiTablePagination-toolbar': {
                fontSize: '0.8rem',
                minHeight: '40px'
              },
              '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                fontSize: '0.8rem'
              }
            }}
          />
          
          {/* 风险提示 */}
          <Box sx={{ 
            p: 2, 
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.1)' : '#fff3e0',
            borderTop: `1px solid ${theme.palette.divider}`,
            borderBottomLeftRadius: '16px',
            borderBottomRightRadius: '16px'
          }}>
            <Typography variant="caption" sx={{ 
              color: theme.palette.mode === 'dark' ? '#ffb74d' : '#f57c00',
              fontSize: '0.75rem',
              fontWeight: 'medium',
              display: 'block',
              textAlign: 'center'
            }}>
              ⚠️ 风险提示：本站所有数据来源于网络，不确保数据准确性，本站内容仅作为学习用途，不作为投资建议，投资有风险，交易需谨慎，祝您投资顺利
            </Typography>
          </Box>
        </>
      )}
      
      {/* 近3日说明弹窗 */}
      <Dialog 
        open={helpDialogOpen} 
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 1, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          background: `linear-gradient(135deg, ${theme.palette.primary.main}20, ${theme.palette.primary.main}10)`,
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          <HelpOutlineIcon color="primary" />
          <Typography variant="h6" component="span" sx={{ fontWeight: 'bold' }}>
            近3日字段说明
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ 
            p: 2, 
            backgroundColor: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.1)' : '#e3f2fd',
            borderRadius: 2,
            border: `1px solid ${theme.palette.primary.main}30`,
            mb: 2
          }}>
            <Typography variant="body1" sx={{ 
              lineHeight: 1.8,
              color: theme.palette.text.primary,
              fontSize: '1rem'
            }}>
              <strong>近3日</strong>是指连续三个交易日内涨跌幅或换手率超过某一个阈值，就需要进行披露。
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ 
            lineHeight: 1.6,
            color: theme.palette.text.secondary,
            fontStyle: 'italic'
          }}>
            💡 <strong>重要提示：</strong>这不一定是当天发生了异动，可能是前几个交易日的累积效应导致的披露要求。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 1 }}>
          <Button 
            onClick={() => setHelpDialogOpen(false)} 
            variant="contained"
            sx={{ 
              borderRadius: 2,
              px: 3,
              textTransform: 'none'
            }}
          >
            我知道了
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
      </Box>
    
    {/* Snackbar for error messages */}
    <Snackbar
      open={snackbarOpen}
      autoHideDuration={6000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    >
      <Alert onClose={() => setSnackbarOpen(false)} severity="error" sx={{ width: '100%' }}>
        {dateError}
      </Alert>
    </Snackbar>
      {/* 添加Snackbar提示 */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
};

export default DataTable;